import React, { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import {
  ColorPaletteAction,
  colorPaletteReducer,
  getSelectedColor,
  IColorPaletteState,
} from 'components/inputs/color-pickers/colorPaletteReducer';
import { IEditorProps } from './Editor';
import { EditorLabel } from './EditorLabel';
import { ColorSwatches } from 'components/inputs/color-pickers/ColorSwatches';
import { ColorPaletteControlButtons } from 'components/inputs/color-pickers/ColorPaletteControlButtons';
import { getChildValue } from 'components/property-editors/utils/ProviderNodeUtils';
import { showLegacyColorPickerDialog } from 'components/inputs/color-pickers/LegacyColorPicker';
import { DesignerColor } from '@decisions/server/Decisions/Silverlight/UI/Utilities';
import { getColorCssString } from 'utils/ColorUtils';
import { IconButton } from 'components/buttons/IconButton';
import { LIST_ITEM_CLASS_NAME } from 'components/lists/ListItem';
import { debounce } from 'debounce';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';

// import './ColorPaletteEditor.less'

export interface IColorPaletteEditorProps extends IEditorProps {}

const getClassName = () => classNames('dp-color-palette-editor');

/**
 * Edit a whole color pallette. This is for places we need something prettier than
 * an array editor for editing sets of colors.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function ColorPaletteEditor({ child, path, id, onChange, hideInputLabel }: IColorPaletteEditorProps) {
  const colors: string[] = getChildValue(child) ?? [];
  const prevColorString = colors.join(',');
  const [state, dispatch] = useReducer(colorPaletteReducer, { colors, selectedIndex: -1 });
  const emptyMessage: string = state.colors.length < 1 ? getEditorConfigValue(child, 'emptyMessage') : '';
  const max = getEditorConfigValue(child, 'max');
  const stateRef = useRef(state);
  stateRef.current = state; // keep an up-to-date ref so we can emit _after_ dispatch
  const disableAdd = state.colors.length >= max && max > 0;
  const onSelectColor = (value: number) => dispatch({ type: ColorPaletteAction.Select, value });

  const emitChange = useMemo(
    () =>
      debounce(async (state: IColorPaletteState) => {
        await onChange(state.colors, path);
      }, 500),
    [path, onChange]
  );

  const handleChange = useCallback(
    (action: { type: ColorPaletteAction; value?: any }) => {
      dispatch(action);
      emitChange.clear();
      // use a timeout to pull the state ref after the next render:
      setTimeout(() => emitChange(stateRef.current));
    },
    [emitChange]
  );

  const onAdd = (e: React.MouseEvent) => {
    showLegacyColorPickerDialog({
      stickTo: e.target as HTMLElement,
      onOk: (designerColor: DesignerColor) => {
        const value = getColorCssString(designerColor);
        handleChange({ type: ColorPaletteAction.Add, value });
      },
      // onClosed: () => {},
      // onClear: () => {
      //   onColorChange?.(index, '');
      // },
      // pickedColor: Color,
    });
  };
  const onEdit = (e: React.MouseEvent) => {
    showLegacyColorPickerDialog({
      stickTo: e.target as HTMLElement,
      onOk: (designerColor: DesignerColor) => {
        const value = getColorCssString(designerColor);
        handleChange({ type: ColorPaletteAction.Update, value });
      },
      // onClosed: () => {},
      onClear: () => {
        handleChange({ type: ColorPaletteAction.Remove });
      },
      pickedColor: getSelectedColor(state),
    });
  };
  const onMoveUp = () => {
    handleChange({ type: ColorPaletteAction.MoveUp });
  };
  const onMoveDown = () => {
    handleChange({ type: ColorPaletteAction.MoveDown });
  };
  const onRemove = () => {
    handleChange({ type: ColorPaletteAction.Remove });
  };

  useEffect(() => {
    dispatch({ type: ColorPaletteAction.SetAll, value: colors });
    return () => {
      // emit last value if un-mounting before it's emitted:
      emitChange.flush();
    };
    // should be safe to only emit if stateColorString changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [/* prevColorString, */ /* state, */ prevColorString]);

  return (
    <EditorLabel
      editor={child}
      path={path}
      htmlFor={id}
      hideInputLabel={hideInputLabel}
      className={getClassName()}
      doNotWrap={true}
      actions={
        <ColorPaletteControlButtons
          {...state}
          // onAdd={onAdd}
          onEdit={onEdit}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onRemove={onRemove}
        />
      }
    >
      <ColorSwatches {...state} onSelectColor={onSelectColor} swatchSize="medium">
        {emptyMessage}&nbsp;
        <IconButton
          iconClassName={`${LIST_ITEM_CLASS_NAME}__add`}
          onClick={onAdd}
          disabled={disableAdd}
          aria-label="Add Color"
        />
      </ColorSwatches>
    </EditorLabel>
  );
}
